// -----------------------------------------------------------------------------
// TaskCardV2 — a single V2 checklist item rendered as a card
//
// Variants:
//   "compact"  — single row, used in modal's stacked checklist list
//   "full"     — wider card with more breathing room, for future Tasks tab use
//
// completion_state: 'OPEN' | 'QUEUED' | 'COMPLETE'
//
// Promoted from src/scratch/followupV2/TaskCardV2.jsx — see Sprint 1 build plan.
// Scratch copy retained for Sprints 2/3 reference; do not delete.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { Box, Stack, Typography } from '@mui/material';
import Iconify from 'src/components/Iconify';
import { ACTION_META, DEFAULT_ACTION_META } from 'src/sections/@dashboard/leadDetail/AttendedGuidedChecklist/actionMeta';

const STATE_CONFIG = {
    OPEN: {
        border: '1px solid rgba(0,0,0,0.08)',
        bgcolor: '#ffffff',
        badgeColor: '#6e6e73',
        badgeBg: 'rgba(0,0,0,0.05)',
        badgeLabel: 'Open',
        badgeIcon: 'mdi:clock-outline',
    },
    QUEUED: {
        border: '1.5px solid rgba(7,141,238,0.35)',
        bgcolor: 'rgba(7,141,238,0.04)',
        badgeColor: '#078DEE',
        badgeBg: 'rgba(7,141,238,0.10)',
        badgeLabel: 'Will complete',
        badgeIcon: 'mdi:check-circle-outline',
    },
    COMPLETE: {
        border: '1px solid rgba(0,171,85,0.25)',
        bgcolor: 'rgba(0,171,85,0.04)',
        badgeColor: '#34C759',
        badgeBg: 'rgba(52,199,89,0.10)',
        badgeLabel: 'Complete',
        badgeIcon: 'mdi:check-bold',
    },
};

export default function TaskCardV2({ item, variant }) {
    const meta = ACTION_META[item.item_type] || DEFAULT_ACTION_META;
    const state = STATE_CONFIG[item.completion_state] || STATE_CONFIG.OPEN;
    const isCompact = variant === 'compact';

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: isCompact ? 1.25 : 1.75,
                px: isCompact ? 1.5 : 2,
                py: isCompact ? 1.25 : 1.75,
                borderRadius: isCompact ? '10px' : '12px',
                border: state.border,
                bgcolor: state.bgcolor,
                transition: 'all 250ms cubic-bezier(0.32,0.72,0,1)',
                // eslint-disable-next-line no-nested-ternary
                boxShadow: item.completion_state === 'QUEUED'
                    ? '0 0 0 3px rgba(7,141,238,0.08)'
                    : item.overdue && item.completion_state === 'OPEN'
                        ? '0 0 0 2px rgba(255,86,48,0.08)'
                        : '0 1px 3px rgba(0,0,0,0.04)',
            }}
        >
            {/* Action-type icon circle */}
            <Box sx={{
                width: isCompact ? 32 : 40,
                height: isCompact ? 32 : 40,
                borderRadius: '50%',
                bgcolor: `${meta.color}18`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                border: `2px solid ${meta.color}2A`,
            }}>
                {item.completion_state === 'COMPLETE' ? (
                    <Iconify icon="mdi:check" width={isCompact ? 15 : 18} sx={{ color: '#34C759' }} />
                ) : (
                    <Iconify icon={meta.icon} width={isCompact ? 15 : 18} sx={{ color: meta.color }} />
                )}
            </Box>

            {/* Title + type chip */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.25 }}>
                    <Typography
                        sx={{
                            fontSize: isCompact ? '13px' : '14px',
                            fontWeight: 600,
                            color: item.completion_state === 'COMPLETE' ? '#6e6e73' : '#1d1d1f',
                            letterSpacing: '-0.01em',
                            textDecoration: item.completion_state === 'COMPLETE' ? 'line-through' : 'none',
                        }}
                    >
                        {item.title}
                    </Typography>
                    {/* Action-type chip — all-caps 10px, action color */}
                    <Box sx={{
                        fontSize: '10px',
                        fontWeight: 600,
                        color: meta.color,
                        bgcolor: `${meta.color}14`,
                        px: 0.625,
                        py: 0.125,
                        borderRadius: '4px',
                        letterSpacing: '0.02em',
                        textTransform: 'uppercase',
                        flexShrink: 0,
                    }}>
                        {meta.label}
                    </Box>
                </Stack>
                {!isCompact && (
                    <Typography sx={{ fontSize: '12px', color: '#6e6e73', mt: 0.25 }}>
                        {item.item_type === 'WHATSAPP_SENT' && 'Log WhatsApp Sent will auto-complete this'}
                        {item.item_type === 'LOG_PHONE_CALL' && 'Log Phone Call will auto-complete this'}
                        {item.item_type === 'LEAD_NEXT_ACTION' && 'Set Next Action will auto-complete this'}
                    </Typography>
                )}
            </Box>

            {/* Right side: due-date pill + state badge */}
            <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
                <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.375,
                    fontSize: '11px',
                    fontWeight: 600,
                    color: item.overdue && item.completion_state === 'OPEN' ? '#FF5630' : '#6e6e73',
                    bgcolor: item.overdue && item.completion_state === 'OPEN' ? 'rgba(255,86,48,0.08)' : 'rgba(0,0,0,0.04)',
                    px: 0.75,
                    py: 0.125,
                    borderRadius: '5px',
                    whiteSpace: 'nowrap',
                }}>
                    <Iconify
                        icon={item.overdue && item.completion_state === 'OPEN' ? 'mdi:alert-circle-outline' : 'mdi:calendar-clock'}
                        width={10}
                    />
                    {item.due_label}
                </Box>
                <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.375,
                    fontSize: '10px',
                    fontWeight: 700,
                    color: state.badgeColor,
                    bgcolor: state.badgeBg,
                    px: 0.75,
                    py: 0.125,
                    borderRadius: '5px',
                    whiteSpace: 'nowrap',
                    letterSpacing: '0.02em',
                }}>
                    <Iconify icon={state.badgeIcon} width={10} />
                    {state.badgeLabel}
                </Box>
            </Stack>
        </Box>
    );
}

TaskCardV2.propTypes = {
    item: PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        item_type: PropTypes.string.isRequired,
        completion_state: PropTypes.oneOf(['OPEN', 'QUEUED', 'COMPLETE']).isRequired,
        due_label: PropTypes.string,
        overdue: PropTypes.bool,
    }).isRequired,
    /** 'compact' = single row (modal list); 'full' = richer card (Tasks tab, future) */
    variant: PropTypes.oneOf(['compact', 'full']),
};

TaskCardV2.defaultProps = {
    variant: 'compact',
};
